import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Lock, ExternalLink } from 'lucide-react';
import API_ENDPOINTS from '../../config/api';
import { useTranslation } from '../../hooks/useTranslation';
import { request, useFutureV2State } from './futureFetch';
import { followsVote, defaultSelection } from './voteHelpers';
import { formatDateTime } from './format';
import WorldRuns from './WorldRuns';

const Wrap = styled.section`
  padding: 24px 32px; display: flex; flex-direction: column; gap: 14px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  @media (max-width: 640px) { padding: 20px 16px; }
  h3 { margin: 0; font-size: 14px; font-weight: 600; color: rgba(255,255,255,0.75); text-transform: uppercase; letter-spacing: 0.06em; }
  p { margin: 0; line-height: 1.5; }
`;
const Muted = styled.p`color: rgba(255,255,255,0.55); font-size: 13px;`;
const Row = styled.label`
  display: grid; grid-template-columns: auto 1fr auto; gap: 10px; align-items: center;
  min-height: 48px; box-sizing: border-box; padding: 8px 10px; border-radius: 8px; border: 1px solid #2c3444; cursor: pointer; font-size: 14px;
  input { width: 20px; height: 20px; }
  b { font-variant-numeric: tabular-nums; }
`;
const Warn = styled.p`
  padding: 8px 12px; border-radius: 8px; font-size: 13px;
  border: 1px solid rgba(245,198,107,0.45); background: rgba(245,198,107,0.08); color: #f5dca8;
`;
const ErrorBox = styled.p`
  padding: 8px 12px; border-radius: 8px; font-size: 13px;
  border: 1px solid rgba(255,107,107,0.5); background: rgba(255,107,107,0.12); color: #ffd9d9;
`;
const Reason = styled.textarea`
  width: 100%; box-sizing: border-box; min-height: 70px; padding: 8px 10px; border-radius: 8px;
  border: 1px solid #2c3444; background: #0f131a; color: #fff; font: inherit; font-size: 14px;
`;
const Actions = styled.div`display: flex; flex-wrap: wrap; gap: 10px; align-items: center;`;
const Button = styled.button`
  all: unset; cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
  min-height: 48px; box-sizing: border-box; padding: 0 16px; border-radius: 8px; font-size: 14px; font-weight: 600;
  color: ${(p) => (p.$primary ? '#0b0d12' : 'rgba(255,255,255,0.85)')};
  background: ${(p) => (p.$primary ? '#c9a7ff' : 'transparent')};
  border: 1px solid ${(p) => (p.$primary ? '#c9a7ff' : '#2c3444')};
  &:disabled { opacity: 0.45; cursor: default; }
`;

/**
 * The facilitator's panel for Tulevaisuustestaus v2 in /admin/workshop (#1474, copied from v1's
 * FutureReimaginedAdmin): the vote standings and the lock. The selection starts from the top
 * half of the vote; changing it so that a better-voted solution is left out needs a reason,
 * which the room sees and the report keeps. Overlaps and conflicts inside the chosen set are
 * warned about, never blocked. Only assessed solutions can be locked.
 */
export default function FutureV2Admin({ instanceId, locked }) {
  const { t, language } = useTranslation();
  const { state, error: loadError, reload } = useFutureV2State(instanceId);
  const [chosen, setChosen] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const solutions = useMemo(() => (state?.solutions || []).filter((s) => s.assessed), [state?.solutions]);
  const counts = useMemo(() => state?.voting?.counts || {}, [state?.voting?.counts]);
  const ranked = useMemo(() => [...solutions].sort((a, b) => (counts[b.id] || 0) - (counts[a.id] || 0)), [solutions, counts]);

  // Start from the vote once it has loaded; keep the facilitator's edits after that.
  useEffect(() => {
    if (state && chosen === null) setChosen(defaultSelection(solutions, counts));
  }, [state, chosen, solutions, counts]);

  if (loadError) return <Wrap><ErrorBox role="alert">{t('futureV2.admin.loadError', { message: loadError.message })}</ErrorBox></Wrap>;
  if (!state || chosen === null) return <Wrap><Muted>{t('futureV2.loading')}</Muted></Wrap>;

  const selected = new Set(chosen);
  const follows = followsVote(chosen, counts, solutions.map((s) => s.id));
  const title = new Map(state.solutions.map((s) => [s.id, s.title]));
  const tensions = (state.interplay?.edges || [])
    .filter((e) => ['overlap', 'conflict'].includes(e.relation) && selected.has(e.a) && selected.has(e.b));
  const toggle = (id) => setChosen((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  const lock = async () => {
    setBusy(true);
    setError(null);
    try {
      await request(API_ENDPOINTS.FUTURE_V2_LOCK(instanceId), {
        method: 'POST',
        body: { solutionIds: chosen, overrideReason: follows ? undefined : reason },
      });
      await reload();
    } catch (e) {
      setError(t('futureV2.admin.lockFailed', { message: e.message }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Wrap>
      <h3>{t('futureV2.admin.title')}</h3>
      <Muted>
        {state.interplay
          ? t(state.interplay.stale ? 'futureV2.admin.interplayStale' : 'futureV2.admin.interplayRead')
          : t('futureV2.admin.interplayMissing')}
      </Muted>
      <Muted>{t('futureV2.admin.votes', { open: t(state.voting.open ? 'futureV2.admin.open' : 'futureV2.admin.closed'), budget: state.voting.budget })}</Muted>

      {ranked.map((s) => (
        <Row key={s.id}>
          <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggle(s.id)} disabled={locked} />
          <span>{s.title}</span>
          <b>{counts[s.id] || 0}</b>
        </Row>
      ))}

      {tensions.map((e) => (
        <Warn key={`${e.a}:${e.b}`}>
          {t(`futureV2.admin.tension.${e.relation}`, { a: title.get(e.a), b: title.get(e.b) })}
        </Warn>
      ))}

      {!follows && (
        <>
          <Muted>{t('futureV2.admin.overrideHint')}</Muted>
          <Reason value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('futureV2.admin.overridePlaceholder')} />
        </>
      )}
      {error && <ErrorBox role="alert">{error}</ErrorBox>}

      <Actions>
        <Button type="button" $primary onClick={lock} disabled={locked || busy || !chosen.length || (!follows && !reason.trim())}>
          <Lock size={14} aria-hidden />
          {t(state.run ? 'futureV2.admin.relock' : 'futureV2.admin.lock', { count: chosen.length })}
        </Button>
        <Button as="a" href={`/tulevaisuustestaus?kid=${instanceId}`} target="_blank" rel="noreferrer">
          <ExternalLink size={14} aria-hidden />
          {t('futureV2.admin.openModule')}
        </Button>
      </Actions>
      {state.run && <WorldRuns instanceId={instanceId} state={state} locked={locked} reload={reload} />}
      {state.run && (
        <Muted>
          {t('futureV2.admin.currentLock', {
            names: state.run.solutionIds.map((id) => title.get(id) || '—').join(', '),
            time: formatDateTime(state.run.lockedAt, language),
          })}
        </Muted>
      )}
    </Wrap>
  );
}
